/**
 * The side column's blocks beyond the Workspace Fan panel's (ADR 0024 §3):
 * Spend on other accounts, AI reasoning, Fan summary by AI and Spenders
 * online.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { crmFetch, dataOf } from '../chat/api'
import { Section } from '../chat/fanBlocks'
import type { SectionsOpen } from '../chat/sectionsOpen'
import { aiStatusLabel, money } from '../chat/format'
import { FeatherIcon } from '../components/FeatherIcon'
import { Spinner } from '../components/Spinner'
import type { CrmAccount } from '../crm/crmAccess'
import { durationLabel, latestActivity, profileText, receptiveness, replyRate, vibe, type AiInsight } from './aiSession'
import { useChatsDeps } from './ChatsDeps'
import { onlineNow, spendersReady, useOnlineSpenders, type Spender } from './onlineSpenders'
import { SpendAcrossOverlay, type SpendRow } from './SpendAcrossOverlay'

type Raw = Record<string, unknown>
/** Open a chat, on this Account or another. */
export type OpenChat = (accountId: string, fanId: string, fanName: string) => void

function shortDate(iso: string | null | undefined, locale: string): string {
  const d = iso ? new Date(iso) : null
  return d && Number.isFinite(d.getTime()) ? d.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : ''
}

/** "just now", "5m ago", "2h ago", "3d ago". */
function agoLabel(at: unknown, nowMs: number, t: (k: string, o?: Record<string, unknown>) => string): string {
  const ms = typeof at === 'number' ? at : Date.parse(String(at ?? ''))
  if (!Number.isFinite(ms) || ms <= 0) return ''
  const sec = Math.max(0, Math.round((nowMs - ms) / 1000))
  if (sec < 45) return t('side.ago.now')
  if (sec < 3600) return t('side.ago.m', { n: Math.round(sec / 60) })
  if (sec < 86400) return t('side.ago.h', { n: Math.round(sec / 3600) })
  return t('side.ago.d', { n: Math.round(sec / 86400) })
}

// ── Spend on other accounts ───────────────────────────────────────────────

export function SpendAcrossBlock({ accountId, fanId, fanName, accounts, sections, onOpenChat }: { accountId: string; fanId: string; fanName: string; accounts: CrmAccount[]; sections: SectionsOpen; onOpenChat?: OpenChat }) {
  const { t, i18n } = useTranslation('chat')
  const [state, setState] = useState<{ total: number; rows: SpendRow[] } | 'loading' | 'error'>('loading')
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    let alive = true
    setState('loading')
    crmFetch('/v1/fans/spend-across', { accountId, local: true, body: { fan_id: fanId, exclude_account_id: accountId } })
      .then((body) => {
        const d = (dataOf(body) || {}) as { total_lifetime?: number; accounts?: SpendRow[] }
        if (alive) setState({ total: Number(d.total_lifetime) || 0, rows: Array.isArray(d.accounts) ? d.accounts : [] })
      })
      .catch(() => alive && setState('error'))
    return () => {
      alive = false
    }
  }, [accountId, fanId])
  const nameOf = (id: string) => {
    const a = accounts.find((x) => x.account_id === id)
    return a?.profile_name || a?.username || id
  }
  return (
    <>
      {expanded && <SpendAcrossOverlay accountId={accountId} fanId={fanId} fanName={fanName} accounts={accounts} onOpenChat={onOpenChat} onClose={() => setExpanded(false)} />}
      <Section
        id="spendAcross"
        icon="layers"
        label={t('side.spendAcross.title')}
        summary={typeof state === 'object' && state.rows.length ? money(state.total) : undefined}
        sections={sections}
        tools={
          <button type="button" className="rc-icon-btn" title={t('side.spendAcross.expand')} aria-label={t('side.spendAcross.expand')} onClick={() => setExpanded(true)}>
            <FeatherIcon name="maximize-2" size={13} />
          </button>
        }
      >
        {state === 'loading' ? (
          <Spinner size={14} />
        ) : state === 'error' ? (
          <span className="rc-error">{t('side.spendAcross.error')}</span>
        ) : !state.rows.length ? (
          <span className="rc-muted">{t('side.spendAcross.none')}</span>
        ) : (
          <div className="rc-side-rows">
            {state.rows.map((r) => (
              <button key={r.account_id} type="button" className="rc-side-row" title={t('side.openOn', { account: nameOf(r.account_id) })} onClick={() => onOpenChat?.(r.account_id, fanId, fanName)}>
                <span className="rc-side-row-main">
                  <b>{nameOf(r.account_id)}</b>
                  <span className="rc-muted">
                    {t('side.spendAcross.last30', { amount: money(r.last_30d) })}
                    {r.last_purchase_amount != null && ` · ${t('side.spendAcross.last', { amount: money(r.last_purchase_amount) })}`}
                    {r.last_purchase_at && ` · ${shortDate(r.last_purchase_at, i18n.language)}`}
                  </span>
                </span>
                <b className="rc-side-row-amount">{money(r.lifetime)}</b>
              </button>
            ))}
          </div>
        )}
      </Section>
    </>
  )
}

// ── AI reasoning + Fan summary by AI ──────────────────────────────────────

function ReceptivenessChip({ value }: { value: unknown }) {
  const { t } = useTranslation('chat')
  const r = receptiveness(value)
  return <span className={`rc-ai-chip rc-ai-recep--${r.level}`}>{r.key ? t(`side.ai.recep.${r.key}`) : r.raw}</span>
}

function VibeChip({ value }: { value: unknown }) {
  const { t } = useTranslation('chat')
  const v = vibe(value)
  if (!v) return null
  return (
    <span className={`rc-ai-chip rc-ai-vibe--${v.key || 'neutral'}`}>
      {v.emoji} {v.key ? t(`side.ai.vibe.${v.key}`) : v.raw}
    </span>
  )
}

export function AiReasoningBlock({ ai, aiStatus, sections }: { ai: AiInsight; aiStatus?: string | null; sections: SectionsOpen }) {
  const { t } = useTranslation('chat')
  const { now } = useChatsDeps()
  const s = ai.session
  const latest = latestActivity(s)
  const rate = s ? replyRate(s.fan_message_count, s.ai_message_count) : null
  const updated = agoLabel(latest?.ts ?? s?.last_message_at, now().getTime(), t)
  const history = Array.isArray(s?.ai_activity) ? s!.ai_activity! : []
  const live = s?.status === 'active'
  return (
    <Section
      id="aiReasoning"
      icon="sparkles"
      label={t('side.ai.reasoning')}
      summary={live ? <span className="rc-ai-live" role="img" aria-label={t('side.ai.live')} /> : undefined}
      sections={sections}
    >
      {!ai.loaded ? (
        <Spinner size={14} />
      ) : !s ? (
        <>
          {aiStatus && <span className="rc-ai-status">{aiStatusLabel(aiStatus, t)}</span>}
          <span className="rc-muted">{t('side.ai.noSession')}</span>
        </>
      ) : (
        <>
          <div className="rc-ai-chips">
            {aiStatus && <span className="rc-ai-status">{aiStatusLabel(aiStatus, t)}</span>}
            <VibeChip value={s.vibe} />
            <ReceptivenessChip value={latest?.receptiveness} />
          </div>
          <span className="rc-fan-note-label">{t('side.ai.thinking')}</span>
          {latest?.reasoning ? <p className="rc-ai-idea">{latest.reasoning}</p> : <span className="rc-muted">{t('side.ai.warming')}</span>}
          <div className="rc-ai-stats">
            <span>
              <b>{String(s.ai_message_count || 0)}</b>
              {t('side.ai.aiMsgs')}
            </span>
            <span>
              <b>{String(s.fan_message_count || 0)}</b>
              {t('side.ai.fanMsgs')}
            </span>
            <span>
              <b>{durationLabel(s.avg_fan_response_seconds)}</b>
              {t('side.ai.avgReply')}
            </span>
            <span>
              <b>{rate == null ? '—' : `${rate}%`}</b>
              {t('side.ai.replyRate')}
            </span>
          </div>
          {history.length > 1 && (
            <details className="rc-ai-timeline">
              <summary>{t('side.ai.timeline', { count: history.length })}</summary>
              <ul>
                {[...history].reverse().map((e, i) => (
                  <li key={i}>
                    <span className="rc-ai-tl-meta">
                      <VibeChip value={e.vibe} />
                      <ReceptivenessChip value={e.receptiveness} />
                      <span className="rc-muted">{agoLabel(e.ts, now().getTime(), t)}</span>
                    </span>
                    <span>{e.reasoning || ''}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {updated && <span className="rc-ai-updated">{t('side.ai.updated', { ago: updated })}</span>}
        </>
      )}
    </Section>
  )
}

const SUMMARY_ROWS: [string, (p: Raw) => unknown][] = [
  ['name', (p) => [p.real_first_name, p.real_surname].filter(Boolean).join(' ') || p.nickname],
  ['age', (p) => p.age],
  ['from', (p) => [p.location_city, p.location_country].filter(Boolean).join(', ') || p.nationality],
  ['family', (p) => p.family_status],
  ['about', (p) => p.family_information],
  ['into', (p) => p.hobbies_and_interests],
  ['whyHere', (p) => p.reason_to_join_onlyfans],
  ['type', (p) => p.fan_type],
  ['mood', (p) => p.fan_current_mood],
  ['callsMe', (p) => p.names_fan_calls_model],
  ['insideJokes', (p) => p.inside_jokes_and_slang],
]
const SUMMARY_TEXTS: [string, string][] = [
  ['summary', 'chat_summary'],
  ['please', 'sex_fetishes_brief_summary'],
  ['suggestion', 'chat_recommendation'],
]

export function AiSummaryBlock({ ai, sections }: { ai: AiInsight; sections: SectionsOpen }) {
  const { t } = useTranslation('chat')
  const p = ai.profile
  const rows = p ? SUMMARY_ROWS.map(([key, pick]) => [key, profileText(pick(p))] as const).filter(([, v]) => v) : []
  const texts = p ? SUMMARY_TEXTS.map(([key, field]) => [key, profileText(p[field])] as const).filter(([, v]) => v) : []
  return (
    <Section id="aiSummary" icon="user" label={t('side.ai.summary')} sections={sections}>
      {!ai.loaded ? (
        <Spinner size={14} />
      ) : !rows.length && !texts.length ? (
        <span className="rc-muted">{t('side.ai.learning')}</span>
      ) : (
        <>
          {rows.length > 0 && (
            <div className="rc-ai-prof">
              {rows.map(([key, value]) => (
                <span key={key} className="rc-ai-prof-row">
                  <span>{t(`side.ai.fields.${key}`)}</span>
                  <span>{value}</span>
                </span>
              ))}
            </div>
          )}
          {texts.map(([key, value]) => (
            <div key={key} className={`rc-ai-text rc-ai-text--${key}`}>
              <span className="rc-fan-note-label">{t(`side.ai.fields.${key}`)}</span>
              <p>{value}</p>
            </div>
          ))}
        </>
      )}
    </Section>
  )
}

// ── Spenders online ───────────────────────────────────────────────────────

const dollars = (v: unknown) => `$${Number.isFinite(Number(v)) ? Number(v).toFixed(2) : '0.00'}`

export function OnlineSpendersBlock({ accountId, sections, onOpenChat }: { accountId: string; sections: SectionsOpen; onOpenChat?: OpenChat }) {
  const { t } = useTranslation('chat')
  const { now } = useChatsDeps()
  const { state, refreshing, refresh } = useOnlineSpenders(accountId)

  const nowMs = now().getTime()
  const ready = spendersReady(state)
  const all = ready ? (state.spenders ?? []) : []
  const online = onlineNow(state)
  const recent = all
    .filter((s) => {
      const seen = Date.parse(s.last_online_at || '')
      return s.is_online !== true && Number.isFinite(seen) && seen >= nowMs - 60 * 60 * 1000
    })
    .sort((a, b) => Date.parse(b.last_online_at!) - Date.parse(a.last_online_at!))
  const source = (s: Spender) => {
    const parts = [s.source_ppv && t('side.spenders.ppv', { amount: dollars(s.ppv_spent) }), s.source_tip && t('side.spenders.tips', { amount: dollars(s.tips_spent) })].filter(Boolean)
    if (parts.length) return parts.join(' · ')
    if (s.source_subscription) return t('side.spenders.activeSub')
    return s.username ? `@${s.username}` : `ID ${s.fan_id}`
  }
  const row = (s: Spender, presence: 'online' | 'recent') => {
    const name = s.name || t('side.spenders.fan', { id: s.fan_id })
    return (
      <button key={s.fan_id} type="button" className={`rc-side-row is-${presence}`} title={t('side.spenders.open', { name })} onClick={() => onOpenChat?.(accountId, String(s.fan_id), name)}>
        <span className="rc-side-row-main">
          <b>{name}</b>
          <span className="rc-muted">{source(s)}</span>
        </span>
        <span className="rc-side-row-aside">
          <b className="rc-side-row-amount">{dollars(s.total_spent)}</b>
          <span className="rc-muted">{presence === 'online' ? t('side.ago.now') : agoLabel(s.last_online_at, nowMs, t)}</span>
        </span>
      </button>
    )
  }

  return (
    <Section
      id="onlineSpenders"
      icon="radio"
      label={t('side.spenders.title')}
      summary={ready ? t('side.spenders.count', { count: online.length }) : undefined}
      tools={
        <button type="button" className="rc-icon-btn" title={t('side.spenders.refresh')} aria-label={t('side.spenders.refresh')} disabled={refreshing} onClick={() => void refresh()}>
          {refreshing ? <Spinner size={12} /> : <FeatherIcon name="refresh-cw" size={13} />}
        </button>
      }
      sections={sections}
    >
      {state === 'loading' ? (
        <Spinner size={14} />
      ) : state === 'error' ? (
        <span className="rc-error">{t('side.spenders.error')}</span>
      ) : state.tracking_enabled !== true ? (
        <span className="rc-muted">{t('side.spenders.trackingOff')}</span>
      ) : state.socket_held_here !== true ? (
        <span className="rc-muted">{t('side.spenders.reconnecting')}</span>
      ) : !online.length && !recent.length ? (
        <span className="rc-muted">{t('side.spenders.nobody')}</span>
      ) : (
        <>
          <span className="rc-fan-note-label">
            {t('side.spenders.online')} · {online.length}
          </span>
          <div className="rc-side-rows">{online.length ? online.map((s) => row(s, 'online')) : <span className="rc-muted">{t('side.spenders.noneOnline')}</span>}</div>
          <span className="rc-fan-note-label">
            {t('side.spenders.recent')} · {recent.length}
          </span>
          <div className="rc-side-rows">{recent.length ? recent.map((s) => row(s, 'recent')) : <span className="rc-muted">{t('side.spenders.noneRecent')}</span>}</div>
        </>
      )}
    </Section>
  )
}
